import { createContext, useContext, useState, useEffect, useCallback } from "react";
import { API_BASE_URL } from "../config/api";
import { safeJson } from "../utils/safeJson";
import { setToken as setAdminServiceToken } from "../services/adminService";
import { setToken as setNfeDeleteToken }   from "../services/nfeDeleteService";
import { setToken as setCteDeleteToken }   from "../services/cteDeleteService";
import { setToken as setUploadXmlToken }        from "../services/uploadXmlService";
import { setToken as setProcessContinueToken }  from "../services/processContinueService";
import { setToken as setAssignmentSaveToken }   from "../services/assignmentSaveService";

const AuthContext = createContext(null);

const STORAGE_KEY = "esc_refresh";

export function AuthProvider({ children }) {
  const [user,        setUser]        = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [loading,     setLoading]     = useState(true);

  // Tenta restaurar sessão via refreshToken salvo
  useEffect(() => {
    const rt = localStorage.getItem(STORAGE_KEY);
    if (!rt) { setLoading(false); return; }
    fetch(`${API_BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: rt }),
    })
      .then(r => r.ok ? safeJson(r) : Promise.reject())
      .then(({ accessToken: at }) => {
        setAccessToken(at);
        return fetch(`${API_BASE_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${at}` },
        }).then(r => safeJson(r));
      })
      .then(setUser)
      .catch(() => localStorage.removeItem(STORAGE_KEY))
      .finally(() => setLoading(false));
  }, []);

  // Mantém services sincronizados com o token atual
  useEffect(() => {
    setAdminServiceToken(accessToken);
    setNfeDeleteToken(accessToken);
    setCteDeleteToken(accessToken);
    setUploadXmlToken(accessToken);
    setProcessContinueToken(accessToken);
    setAssignmentSaveToken(accessToken);
  }, [accessToken]);

  const login = useCallback(async (email, password) => {
    const r    = await fetch(`${API_BASE_URL}/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    const data = await safeJson(r);
    if (!r.ok) throw new Error(data.error || "Erro ao autenticar");
    localStorage.setItem(STORAGE_KEY, data.refreshToken);
    setAccessToken(data.accessToken);
    setUser(data.user);
    return data.user;
  }, []);

  // Usado pelo retorno do SSO (Microsoft/SAP) — o backend já validou a identidade e devolveu
  // o mesmo formato de { accessToken, refreshToken, user } do login local.
  const loginWithTokenData = useCallback((data) => {
    localStorage.setItem(STORAGE_KEY, data.refreshToken);
    setAccessToken(data.accessToken);
    setUser(data.user);
  }, []);

  const logout = useCallback(async () => {
    const rt = localStorage.getItem(STORAGE_KEY);
    localStorage.removeItem(STORAGE_KEY);
    setAccessToken(null);
    setUser(null);
    if (rt) fetch(`${API_BASE_URL}/auth/logout`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refreshToken: rt }),
    }).catch(() => {});
  }, []);

  // Helper para chamadas autenticadas
  const authFetch = useCallback((url, opts = {}) => {
    return fetch(url, {
      ...opts,
      headers: { ...opts.headers, Authorization: `Bearer ${accessToken}` },
    });
  }, [accessToken]);

  const hasRole = useCallback((...roles) => roles.includes(user?.role), [user]);

  const hasCompany = useCallback((cnpj) => {
    if (!user) return false;
    if (user.role === "SUPER") return true;
    return user.companies?.some(c => (typeof c === "string" ? c : c.cnpj) === cnpj);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, accessToken, loading, login, loginWithTokenData, logout, authFetch, hasRole, hasCompany }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
